/**
 * seasonalAccent.js
 * Computes the current seasonal accent color via OKLCH interpolation
 * between four annual anchor points.
 *
 * Math reference: Björn Ottosson's OKLab specification
 * https://bottosson.github.io/posts/oklab/
 */

// ── Anchor definitions ────────────────────────────────────────────────────────
// Each anchor: { month (1-based), day, base, deep }
const ANCHORS = [
  { month: 3,  day: 20, base: '#A3AB95', deep: '#7A8270' }, // Spring
  { month: 6,  day: 21, base: '#C18560', deep: '#74452A' }, // Summer
  { month: 9,  day: 22, base: '#B56741', deep: '#8B4A2D' }, // Fall
  { month: 12, day: 21, base: '#8C4538', deep: '#4D1F19' }, // Winter
];

// ── sRGB ↔ Linear ─────────────────────────────────────────────────────────────
function toLinear(x) {
  return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
}

function toSrgb(x) {
  return x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055;
}

// ── Hex → OKLCH ───────────────────────────────────────────────────────────────
export function hexToOklch(hex) {
  // 1. Hex → sRGB [0,1]
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  // 2. sRGB → linear
  const rl = toLinear(r);
  const gl = toLinear(g);
  const bl = toLinear(b);

  // 3. Linear sRGB → OKLab (Ottosson matrices)
  const l = 0.4122214708 * rl + 0.5363325363 * gl + 0.0514459929 * bl;
  const m = 0.2119034982 * rl + 0.6806995451 * gl + 0.1073969566 * bl;
  const s = 0.0883024619 * rl + 0.2817188376 * gl + 0.6299787005 * bl;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  const L =  0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
  const a =  1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
  const bk = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;

  // 4. OKLab → OKLCH
  const C = Math.sqrt(a * a + bk * bk);
  let h = Math.atan2(bk, a) * (180 / Math.PI);
  if (h < 0) h += 360;

  return { L, C, h };
}

// ── OKLCH → Hex ───────────────────────────────────────────────────────────────
export function oklchToHex({ L, C, h }) {
  // 1. OKLCH → OKLab
  const hRad = h * (Math.PI / 180);
  const a = C * Math.cos(hRad);
  const b = C * Math.sin(hRad);

  // 2. OKLab → linear sRGB (Ottosson inverse)
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  let rl =  4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  let gl = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  let bl = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;

  // 3. Linear → sRGB, clamp to [0,1]
  const r = Math.max(0, Math.min(1, toSrgb(rl)));
  const g = Math.max(0, Math.min(1, toSrgb(gl)));
  const bv = Math.max(0, Math.min(1, toSrgb(bl)));

  // 4. sRGB → hex
  const toHex = x => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(bv)}`;
}

// ── OKLCH interpolation ───────────────────────────────────────────────────────
function interpolateOklch(ok1, ok2, t) {
  const L = ok1.L + (ok2.L - ok1.L) * t;
  const C = ok1.C + (ok2.C - ok1.C) * t;

  // Shortest hue path (handle 360° wrap)
  let dh = ok2.h - ok1.h;
  if (dh > 180)  dh -= 360;
  if (dh < -180) dh += 360;
  let h = ok1.h + dh * t;
  if (h < 0)   h += 360;
  if (h >= 360) h -= 360;

  return { L, C, h };
}

// ── Season transition calculator ──────────────────────────────────────────────
function anchorMs(year, month, day) {
  return new Date(year, month - 1, day).getTime();
}

function dateMs(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function getSeasonTransition(date) {
  const year = date.getFullYear();
  const ms   = dateMs(date);

  // Build anchor timestamps for this year
  const thisYearAnchors = ANCHORS.map(a => ({
    ...a,
    ms: anchorMs(year, a.month, a.day),
  }));

  // Previous year's winter anchor (for Jan–Mar 19 dates)
  const prevWinterAnchor = {
    ...ANCHORS[3],
    ms: anchorMs(year - 1, ANCHORS[3].month, ANCHORS[3].day),
  };

  // Next year's spring anchor (for Dec 21–Dec 31 dates)
  const nextSpringAnchor = {
    ...ANCHORS[0],
    ms: anchorMs(year + 1, ANCHORS[0].month, ANCHORS[0].day),
  };

  const candidates = [prevWinterAnchor, ...thisYearAnchors, nextSpringAnchor];

  // Find the two candidates that bracket the date
  let fromAnchor, toAnchor;
  for (let i = 0; i < candidates.length - 1; i++) {
    if (ms >= candidates[i].ms && ms < candidates[i + 1].ms) {
      fromAnchor = candidates[i];
      toAnchor   = candidates[i + 1];
      break;
    }
  }

  // Edge case: exactly on or past the last candidate (Dec 21 → Dec 31)
  if (!fromAnchor) {
    fromAnchor = candidates[candidates.length - 2];
    toAnchor   = candidates[candidates.length - 1];
  }

  const span     = toAnchor.ms - fromAnchor.ms;
  const elapsed  = ms - fromAnchor.ms;
  const fraction = span > 0 ? Math.min(1, Math.max(0, elapsed / span)) : 0;

  return { fromAnchor, toAnchor, fraction };
}

// ── Public exports ────────────────────────────────────────────────────────────

/**
 * Returns the interpolated base accent color for a given date.
 * @param {Date} date - Defaults to new Date() if not provided
 * @returns {string} Hex color e.g. "#B56741"
 */
export function getCurrentAccent(date = new Date()) {
  const { fromAnchor, toAnchor, fraction } = getSeasonTransition(date);
  if (fraction === 0) return fromAnchor.base;
  if (fraction === 1) return toAnchor.base;
  const ok1 = hexToOklch(fromAnchor.base);
  const ok2 = hexToOklch(toAnchor.base);
  return oklchToHex(interpolateOklch(ok1, ok2, fraction));
}

/**
 * Returns the interpolated deep accent color for a given date.
 * @param {Date} date - Defaults to new Date() if not provided
 * @returns {string} Hex color
 */
export function getCurrentAccentDeep(date = new Date()) {
  const { fromAnchor, toAnchor, fraction } = getSeasonTransition(date);
  if (fraction === 0) return fromAnchor.deep;
  if (fraction === 1) return toAnchor.deep;
  const ok1 = hexToOklch(fromAnchor.deep);
  const ok2 = hexToOklch(toAnchor.deep);
  return oklchToHex(interpolateOklch(ok1, ok2, fraction));
}

// ── Inline tests (run with: node -e "import('./seasonalAccent.js').then(m=>m._runTests())") ──

export function _runTests() {
  let pass = 0, fail = 0;

  function hexToRgb(hex) {
    return [
      parseInt(hex.slice(1,3),16),
      parseInt(hex.slice(3,5),16),
      parseInt(hex.slice(5,7),16),
    ];
  }

  function closeEnough(a, b, tolerance = 2) {
    const ra = hexToRgb(a), rb = hexToRgb(b);
    return ra.every((v, i) => Math.abs(v - rb[i]) <= tolerance);
  }

  function test(label, actual, expected, isMidpoint = false) {
    if (isMidpoint) {
      // Midpoint: must NOT equal either anchor
      const notFrom = !closeEnough(actual, expected[0], 2);
      const notTo   = !closeEnough(actual, expected[1], 2);
      const ok = notFrom && notTo;
      console.log(`${ok ? '✓' : '✗'} ${label}: ${actual} (not ${expected[0]} or ${expected[1]})`);
      ok ? pass++ : fail++;
    } else {
      const ok = closeEnough(actual, expected);
      console.log(`${ok ? '✓' : '✗'} ${label}: got ${actual}, expected ${expected}`);
      ok ? pass++ : fail++;
    }
  }

  // Boundary tests — base accent
  test('Spring anchor base',  getCurrentAccent(new Date('2026-03-20')), '#A3AB95');
  test('Summer anchor base',  getCurrentAccent(new Date('2026-06-21')), '#C18560');
  test('Fall anchor base',    getCurrentAccent(new Date('2026-09-22')), '#B56741');
  test('Winter anchor base',  getCurrentAccent(new Date('2026-12-21')), '#8C4538');

  // Boundary tests — deep accent
  test('Spring anchor deep',  getCurrentAccentDeep(new Date('2026-03-20')), '#7A8270');
  test('Summer anchor deep',  getCurrentAccentDeep(new Date('2026-06-21')), '#74452A');
  test('Fall anchor deep',    getCurrentAccentDeep(new Date('2026-09-22')), '#8B4A2D');
  test('Winter anchor deep',  getCurrentAccentDeep(new Date('2026-12-21')), '#4D1F19');

  // Midpoint tests — interpolation produces intermediate values
  test('Mid spring→summer (May 6) base',
    getCurrentAccent(new Date('2026-05-06')),
    ['#A3AB95', '#C18560'], true);

  test('Mid fall→winter (Nov 7) base',
    getCurrentAccent(new Date('2026-11-07')),
    ['#B56741', '#8C4538'], true);

  // Winter wrap test — Feb 4, 2027 should be between Dec 21 2026 and Mar 20 2027
  const feb4 = getCurrentAccent(new Date('2027-02-04'));
  const isWineToSageIntermediate = feb4 !== '#8C4538' && feb4 !== '#A3AB95';
  console.log(`${isWineToSageIntermediate ? '✓' : '✗'} Winter wrap (Feb 4 2027): ${feb4} (wine→sage intermediate)`);
  isWineToSageIntermediate ? pass++ : fail++;

  console.log(`\n${pass} passed, ${fail} failed`);
  return { pass, fail };
}
